// SPDX-License-Identifier: MIT
/**
 * Point clouds: sample points from text, filled paths or primitives, then morph between shapes.
 * Sampling is memoised per input (pure function of its arguments), so it is safe across frames.
 */
import { TAU } from '../core/math.ts';
import { mulberry32 } from '../core/rng.ts';
import { type Pt, type Subpath, tracePath } from './path.ts';
const cache = new Map<string, Pt[]>();
function memo(key: string, make: () => Pt[]): Pt[] {
    let v = cache.get(key);
    if (!v) {
        v = make();
        cache.set(key, v);
    }
    return v;
}
/** Keep at most `max` points, spread evenly through the list (deterministic). */
export function thin(pts: Pt[], max: number): Pt[] {
    if (pts.length <= max)
        return pts;
    return Array.from({ length: max }, (_, i) => pts[Math.floor((i * pts.length) / max)]!);
}
function rasterPoints(w: number, h: number, step: number, paint: (c: OffscreenCanvasRenderingContext2D) => void) {
    const off = new OffscreenCanvas(w, h);
    const c = off.getContext('2d', { willReadFrequently: true })!;
    c.fillStyle = '#fff';
    paint(c);
    const data = c.getImageData(0, 0, w, h).data;
    const pts: Pt[] = [];
    for (let y = 0; y < h; y += step)
        for (let x = 0; x < w; x += step)
            if (data[(y * w + x) * 4 + 3]! > 140)
                pts.push([x - w / 2, y - h / 2]);
    return pts;
}
/** Points filling `text` rendered with `font`, centred on the origin. */
export function fromText(text: string, font: string, { step = 5, max = 1600, width = 1800, height = 400 } = {}): Pt[] {
    return memo(`t|${font}|${text}|${step}|${max}|${width}|${height}`, () => thin(rasterPoints(width, height, step, (c) => {
        c.font = font;
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillText(text, width / 2, height / 2);
    }), max));
}
/**
 * Points filling vector subpaths (e.g. a fitted logo) — subpaths must already be in design space;
 * returned points are absolute. `key` identifies the shape for memoisation.
 */
export function fromPaths(key: string, subs: Subpath[], { step = 5, max = 1600, fillRule = 'evenodd', }: {
    step?: number;
    max?: number;
    fillRule?: CanvasFillRule;
} = {}): Pt[] {
    return memo(`p|${key}|${step}|${max}|${fillRule}`, () => {
        const W = 1920;
        const H = 1080;
        const pts = rasterPoints(W, H, step, (c) => {
            tracePath(c as unknown as CanvasRenderingContext2D, subs);
            c.fill(fillRule);
        });
        return thin(pts.map(([x, y]) => [x + W / 2, y + H / 2] as Pt), max);
    });
}
/** n points on a sphere of radius r (golden spiral — even coverage), as 3D points. */
export function sphere(n: number, r: number): Array<[
    number,
    number,
    number
]> {
    const golden = Math.PI * (3 - Math.sqrt(5));
    return Array.from({ length: n }, (_, i) => {
        const y = 1 - (i / Math.max(1, n - 1)) * 2;
        const rad = Math.sqrt(1 - y * y);
        const a = golden * i;
        return [Math.cos(a) * rad * r, y * r, Math.sin(a) * rad * r];
    });
}
/** n points scattered uniformly in a disc (seeded). */
export function disc(n: number, r: number, seed = 1): Pt[] {
    const rnd = mulberry32(seed);
    return Array.from({ length: n }, () => {
        const a = rnd() * TAU;
        const d = Math.sqrt(rnd()) * r;
        return [Math.cos(a) * d, Math.sin(a) * d] as Pt;
    });
}
/**
 * Pair two point sets of different sizes for morphing: both are sorted along the same direction
 * so points travel in coherent streams instead of crossing randomly. Returns equal-length lists.
 */
export function pair(from: Pt[], to: Pt[], angle = 0.6): [
    Pt[],
    Pt[]
] {
    const n = Math.max(from.length, to.length);
    const key = ([x, y]: Pt) => x * Math.cos(angle) + y * Math.sin(angle);
    const a = [...from].sort((p, q) => key(p) - key(q));
    const b = [...to].sort((p, q) => key(p) - key(q));
    const pick = (list: Pt[], i: number) => list[Math.floor((i * list.length) / n)]!;
    return [Array.from({ length: n }, (_, i) => pick(a, i)), Array.from({ length: n }, (_, i) => pick(b, i))];
}
/** Draw square dots fast (fillRect is the cheapest primitive on a CPU rasteriser). */
export function dots(ctx: CanvasRenderingContext2D, pts: Pt[], size: number): void {
    const h = size / 2;
    for (const [x, y] of pts)
        ctx.fillRect(x - h, y - h, size, size);
}
